import { useEffect } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import dayjs from 'dayjs';
import type { EventWithCircuit } from '../../api/eventsService';
import type { StationData } from '../../api/stationObservationsService';
import type { RadarTile } from './useDataFetching';
import {
  addWindArrowImages,
  hasAllWindArrowImages,
  getWindSpeedKmh,
  getWindGustKmh,
  getGeographicWindDirection,
  buildFullIconImageExpression,
  buildFullIconSizeExpressionWithZoom,
  buildTextFieldExpression,
  buildTextColorExpression,
} from './windArrowUtils';
import { ZOOM_WIND_LABEL_SIZE } from './mapZoomScaleUtils';

export interface WindArrowLayerIds {
  windArrowsSource: string;
  windArrowsLayer: string;
  windArrowImagePrefix: string;
  stationsLayer: string;
}

export interface UseWindArrowsLayerParams {
  mapRef: React.RefObject<MapboxMap | null>;
  enabled: boolean;
  selectedEvent: EventWithCircuit | null;
  showWindArrows: boolean;
  stations: StationData[];
  tiles: RadarTile[];
  currentIndex: number;
  styleVersion: number;
  ids: WindArrowLayerIds;
}

const MAX_RETRIES = 20;
const RETRY_DELAY = 50;

/**
 * Reusable hook for wind arrow layers on any Nowcasting map instance.
 *
 * Draws a symbol layer with speed/gust-colored arrows at each weather
 * station, synced to the current radar frame time. Pass `ids` to
 * control Mapbox source/layer/image names per map role.
 */
export function useWindArrowsLayer({
  mapRef,
  enabled,
  selectedEvent,
  showWindArrows,
  stations,
  tiles,
  currentIndex,
  styleVersion,
  ids,
}: UseWindArrowsLayerParams): void {
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedEvent?.stations || !showWindArrows || !enabled) {
      if (map && map.getLayer(ids.windArrowsLayer)) {
        map.removeLayer(ids.windArrowsLayer);
      }
      if (map && map.getSource(ids.windArrowsSource)) {
        map.removeSource(ids.windArrowsSource);
      }
      return;
    }

    let retryCount = 0;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const updateWindArrows = async () => {
      if (!map.isStyleLoaded()) {
        retryCount++;
        if (retryCount < MAX_RETRIES) {
          timeoutId = setTimeout(updateWindArrows, RETRY_DELAY);
        }
        return;
      }

      if (!hasAllWindArrowImages(map, ids.windArrowImagePrefix)) {
        await addWindArrowImages(map, ids.windArrowImagePrefix);
      }

      const currentTime = tiles.length > 0 && currentIndex >= 0 && currentIndex < tiles.length
        ? tiles[currentIndex].datetime
        : null;

      if (map.getLayer(ids.windArrowsLayer)) {
        map.removeLayer(ids.windArrowsLayer);
      }
      if (map.getSource(ids.windArrowsSource)) {
        map.removeSource(ids.windArrowsSource);
      }

      const windArrowFeatures: GeoJSON.Feature[] = [];

      selectedEvent.stations.forEach((station) => {
        const stationData = stations.find(s => s.station_reference === station.reference);
        if (!stationData || !stationData.observations || stationData.observations.length === 0) {
          return;
        }

        let closestObs = stationData.observations[0];
        if (currentTime) {
          let minDiff = Math.abs(dayjs(stationData.observations[0].reading_timestamp).diff(currentTime));
          for (const obs of stationData.observations) {
            const diff = Math.abs(dayjs(obs.reading_timestamp).diff(currentTime));
            if (diff < minDiff) {
              minDiff = diff;
              closestObs = obs;
            }
          }
        }

        const windDirection = getGeographicWindDirection(stationData, closestObs);

        if (windDirection !== null) {
          const windSpeedKmh = getWindSpeedKmh(stationData, closestObs);
          const windGustKmh = getWindGustKmh(stationData, closestObs);

          windArrowFeatures.push({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [station.lon, station.lat],
            },
            properties: {
              rotation: windDirection,
              reference: station.reference,
              windSpeedKmh: windSpeedKmh ?? -1,
              windGustKmh: windGustKmh ?? -1,
            },
          });
        }
      });

      if (windArrowFeatures.length === 0) return;

      map.addSource(ids.windArrowsSource, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: windArrowFeatures,
        },
      });

      map.addLayer({
        id: ids.windArrowsLayer,
        type: 'symbol',
        source: ids.windArrowsSource,
        layout: {
          'icon-image': buildFullIconImageExpression(ids.windArrowImagePrefix),
          'icon-size': buildFullIconSizeExpressionWithZoom(),
          'icon-rotate': ['get', 'rotation'],
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-anchor': 'bottom',
          'text-field': buildTextFieldExpression(),
          'text-size': ZOOM_WIND_LABEL_SIZE,
          'text-offset': [0, 3.5],
          'text-anchor': 'top',
          'text-allow-overlap': true,
          'text-ignore-placement': true,
          'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
        },
        paint: {
          'icon-opacity': 0.9,
          'text-color': buildTextColorExpression(),
          'text-halo-color': '#ffffff',
          'text-halo-width': 2,
        },
      });

      const enforceLayerOrder = () => {
        if (map.getLayer(ids.stationsLayer) && map.getLayer(ids.windArrowsLayer)) {
          map.moveLayer(ids.windArrowsLayer, ids.stationsLayer);
        }
      };
      enforceLayerOrder();
      map.once('idle', enforceLayerOrder);
    };

    updateWindArrows();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [showWindArrows, selectedEvent, stations, tiles, currentIndex, styleVersion, mapRef, enabled, ids]);
}
